/**
 * KpiCard — Unified KPI card (Design C).
 *
 * Consolidates 4 prior variants (ui, charts, data, finance) into one component.
 * Uses .kpi-card CSS class as base + Tailwind overrides via cn().
 *
 * Features:
 *   - Benchmark left-border coloring (good / warning / critical)
 *   - Delta: numeric change% OR raw string/number with directional arrow
 *   - Loading skeleton with pulse animation
 *   - Compact mode (smaller padding + fonts)
 *   - Mini bar chart (8-bar sparkline, last bar highlighted)
 *   - SVG sparkline (polyline)
 *   - valueColor override
 *   - formatMetricValue when unit is provided with numeric value
 */
import React from 'react';
import { cn } from '../../lib/utils';
import { formatMetricValue } from '../../config/chartTheme';

export interface KpiCardProps {
  label: string;
  value: number | string;
  unit?: string;
  // Delta - supports both simple and structured
  delta?: string | number | null;
  deltaLabel?: string;
  deltaDirection?: 'up' | 'down' | 'neutral';
  change?: number | null;
  changeLabel?: string;
  // Visual
  valueColor?: string;
  benchmark?: 'good' | 'warning' | 'critical' | null;
  sparklineData?: number[];
  miniChart?: number[];
  // Behavior
  onClick?: () => void;
  loading?: boolean;
  compact?: boolean;
  className?: string;
}

const BENCHMARK_BORDER: Record<string, string> = {
  good: '#10B981',
  warning: '#F59E0B',
  critical: '#EF4444',
};

/* ── Tiny SVG sparkline ───────────────────────── */

function Sparkline({ data }: { data: number[] }) {
  if (data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const h = 28;
  const w = data.length - 1;
  const points = data
    .map((v, i) => `${(i / w) * 100},${h - ((v - min) / range) * (h - 4) - 2}`)
    .join(' ');

  return (
    <svg
      viewBox={`0 0 100 ${h}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: h, display: 'block', marginTop: 8 }}
    >
      <polyline
        points={points}
        fill="none"
        stroke="#94A3B8"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Component ────────────────────────────────── */

export default function KpiCard({
  label,
  value,
  unit,
  delta,
  deltaLabel,
  deltaDirection,
  change,
  changeLabel,
  benchmark,
  sparklineData,
  miniChart,
  onClick,
  loading,
  compact,
  className,
  valueColor,
}: KpiCardProps) {
  /* ── Loading skeleton ─────────────────────────── */
  if (loading) {
    return (
      <div
        className={cn('kpi-card', className)}
        style={{ textAlign: 'center', padding: compact ? '12px 14px' : undefined }}
      >
        <div style={{
          height: 12, width: '50%', margin: '0 auto 12px',
          background: 'var(--bg-elevated)', borderRadius: 4,
          animation: 'pulse 1.5s ease-in-out infinite',
        }} />
        <div style={{
          height: 28, width: '60%', margin: '0 auto 8px',
          background: 'var(--bg-elevated)', borderRadius: 4,
          animation: 'pulse 1.5s ease-in-out infinite',
        }} />
        <div style={{
          height: 12, width: '40%', margin: '0 auto',
          background: 'var(--bg-elevated)', borderRadius: 4,
          animation: 'pulse 1.5s ease-in-out infinite',
        }} />
      </div>
    );
  }

  /* ── Benchmark left-border ────────────────────── */
  const benchmarkStyle: React.CSSProperties = benchmark
    ? { borderLeft: `3px solid ${BENCHMARK_BORDER[benchmark]}` }
    : {};

  /* ── Format value ─────────────────────────────── */
  let displayValue: string;
  if (typeof value === 'number' && unit) {
    displayValue = formatMetricValue(value, unit);
  } else if (typeof value === 'number') {
    displayValue = value.toLocaleString();
  } else {
    displayValue = value;
  }

  /* ── Delta / change rendering ─────────────────── */
  let deltaNode: React.ReactNode = null;

  if (change != null) {
    // Numeric change% (from charts/finance variants)
    const color = change >= 0 ? 'var(--positive)' : 'var(--negative)';
    const arrow = change > 0 ? '\u25B2' : change < 0 ? '\u25BC' : '';
    deltaNode = (
      <div style={{
        fontSize: 12, fontWeight: 500, color, marginTop: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
      }}>
        <span>{arrow} {change > 0 ? '+' : ''}{change.toFixed(1)}%</span>
        {changeLabel && (
          <span style={{ color: 'var(--text-hint)', fontSize: 11 }}>{changeLabel}</span>
        )}
      </div>
    );
  } else if (delta != null) {
    // Simple delta (string or number) with optional direction
    const dir = deltaDirection || 'neutral';
    const deltaColor = dir === 'up' ? 'var(--positive)'
      : dir === 'down' ? 'var(--warning)'
      : 'var(--text-muted)';
    const arrow = dir === 'up' ? '\u25B2' : dir === 'down' ? '\u25BC' : '';
    deltaNode = (
      <div className={`kpi-delta ${dir}`} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        fontSize: 12, color: deltaColor, marginTop: 6,
      }}>
        <span>{arrow} {typeof delta === 'number' ? `${Math.abs(delta).toFixed(1)}%` : delta}</span>
        {deltaLabel && <span style={{ color: 'var(--text-hint)', fontSize: 11 }}>{deltaLabel}</span>}
      </div>
    );
  }

  return (
    <div
      className={cn('kpi-card', className)}
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        padding: compact ? '12px 14px' : undefined,
        textAlign: 'center' as const,
        ...benchmarkStyle,
      }}
    >
      <div className="kpi-label">{label}</div>
      <div
        className="kpi-value"
        style={{
          color: valueColor || 'var(--text-primary)',
          fontSize: compact ? 18 : undefined,
        }}
      >
        {displayValue}
      </div>

      {deltaNode}

      {/* Mini bar chart */}
      {miniChart && miniChart.length > 0 && (
        <div style={{
          marginTop: 12, height: 28, display: 'flex', alignItems: 'flex-end', gap: 2,
        }}>
          {miniChart.map((v, i) => {
            const max = Math.max(...miniChart);
            const pct = max > 0 ? (v / max) * 100 : 0;
            const isLast = i === miniChart.length - 1;
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: `${Math.max(pct, 4)}%`,
                  borderRadius: 2,
                  background: isLast ? 'var(--navy-800)' : 'var(--accent-subtle)',
                  transition: 'height 0.3s ease',
                }}
              />
            );
          })}
        </div>
      )}

      {/* SVG sparkline */}
      {sparklineData && sparklineData.length >= 2 && (
        <Sparkline data={sparklineData} />
      )}
    </div>
  );
}

export { KpiCard };
